import { apiClient } from "../../../lib/apiClient";

export interface PreguntaPAES {
  idPreguntaPAES: number;
  contenido: string;
  respuesta: string;
  explicacion: string;
  anio: number;
  periodo: string;
  numero: number;
  tipo: string;
  asignaturaId: number;
  dificultad: number;
  tiempoEstimado: number;
  tags: string[];
  ejeTematico?: string;
  fuente?: string;
  alternativas?: any;
  imagenUrl?: string;
  datosGrafico?: any;
  tipoSimulacion?: string;
  numeroIntentos: number;
  vecesRespondida: number;
  porcentajeCorrectas: number;
  revisada: boolean;
  creadoPor?: string;
  asignatura?: {
    idAsignatura: number;
    nombre: string;
    descripcion?: string;
  };
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PreguntasPAESResponse {
  preguntas: PreguntaPAES[];
  pagination: PaginationInfo;
}

export interface EstadisticasPAESM1 {
  totalPreguntas: number;
  preguntasPorAnio: Array<{
    anio: number;
    _count: {
      idPreguntaPAES: number;
    };
  }>;
  preguntasPorDificultad: Array<{
    dificultad: number;
    _count: {
      idPreguntaPAES: number;
    };
  }>;
}

export interface CreatePreguntaPAESData {
  contenido: string;
  respuesta: string;
  explicacion: string;
  anio: number;
  periodo: string;
  numero: number;
  tipo: string;
  asignaturaId: number;
  dificultad?: number;
  tiempoEstimado?: number;
  tags?: string[];
  ejeTematico?: string;
  fuente?: string;
  alternativas?: any;
  imagenUrl?: string;
  datosGrafico?: any;
  tipoSimulacion?: string;
  revisada?: boolean;
  imagen?: File;
}

export interface UpdatePreguntaPAESData
  extends Partial<CreatePreguntaPAESData> {}

export class SimulacionesService {
  /**
   * Obtener preguntas PAES M1 con paginación y filtros
   * Filtra solo preguntas con estado 'EXAMEN' y tipoSimulacion = 'M1'
   */
  static async getPreguntasPAESM1(params?: {
    page?: number;
    limit?: number;
    anio?: number;
    periodo?: string;
    dificultad?: number;
  }): Promise<PreguntasPAESResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.anio) searchParams.append("anio", params.anio.toString());
    if (params?.periodo) searchParams.append("periodo", params.periodo);
    if (params?.dificultad)
      searchParams.append("dificultad", params.dificultad.toString());

    const response = await apiClient.get(
      `/simulaciones/preguntas-paes-m1?${searchParams.toString()}`
    );
    return response.data;
  }

  /**
   * Obtener preguntas para simulaciones
   * Filtra solo preguntas con estado 'SIMULACION' (cualquier tipoSimulacion)
   */
  static async getPreguntasSimulaciones(params?: {
    page?: number;
    limit?: number;
    anio?: number;
    periodo?: string;
    dificultad?: number;
  }): Promise<PreguntasPAESResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.anio) searchParams.append("anio", params.anio.toString());
    if (params?.periodo) searchParams.append("periodo", params.periodo);
    if (params?.dificultad)
      searchParams.append("dificultad", params.dificultad.toString());

    const response = await apiClient.get(
      `/simulaciones/simulaciones?${searchParams.toString()}`
    );
    return response.data;
  }

  /**
   * Obtener preguntas PAES M2 con paginación y filtros
   * Filtra solo preguntas con estado 'EXAMEN' y tipoSimulacion = 'M2'
   */
  static async getPreguntasPAESM2(params?: {
    page?: number;
    limit?: number;
    anio?: number;
    periodo?: string;
    dificultad?: number;
  }): Promise<PreguntasPAESResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.anio) searchParams.append("anio", params.anio.toString());
    if (params?.periodo) searchParams.append("periodo", params.periodo);
    if (params?.dificultad)
      searchParams.append("dificultad", params.dificultad.toString());

    const response = await apiClient.get(
      `/simulaciones/preguntas-paes-m2?${searchParams.toString()}`
    );
    return response.data;
  }

  /**
   * Obtener una pregunta PAES específica por ID
   */
  static async getPreguntaPAESPorId(id: number): Promise<PreguntaPAES> {
    const response = await apiClient.get(
      `/simulaciones/preguntas-paes-m1/${id}`
    );
    return response.data;
  }

  /**
   * Obtener estadísticas de preguntas PAES M1
   */
  static async getEstadisticasPAESM1(): Promise<EstadisticasPAESM1> {
    const response = await apiClient.get("/simulaciones/estadisticas-paes-m1");
    return response.data;
  }

  /**
   * Importar preguntas PAES desde un archivo
   */
  static async importarPreguntasPAES(
    file: File
  ): Promise<{ message: string; imported: number }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post(
      "/simulaciones/importar-preguntas-paes",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  /**
   * Importar preguntas PAES desde JSON
   */
  static async importarPreguntasPAESDesdeJSON(
    preguntas: CreatePreguntaPAESData[]
  ): Promise<{
    message: string;
    importadas: number;
    total: number;
    preguntasImportadas: Array<{ id: number; numero: number }>;
    errores: Array<{ numero: number; error: string }>;
  }> {
    const response = await apiClient.post(
      "/simulaciones/importar-preguntas-paes",
      {
        preguntas,
      }
    );
    return response.data;
  }

  /**
   * Exportar preguntas PAES a Excel
   */
  static async exportarPreguntasPAES(filtros?: {
    anio?: number;
    periodo?: string;
    dificultad?: number;
  }): Promise<Blob> {
    const searchParams = new URLSearchParams();

    if (filtros?.anio) searchParams.append("anio", filtros.anio.toString());
    if (filtros?.periodo) searchParams.append("periodo", filtros.periodo);
    if (filtros?.dificultad)
      searchParams.append("dificultad", filtros.dificultad.toString());

    const response = await apiClient.get(
      `/simulaciones/exportar-preguntas-paes?${searchParams.toString()}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  }

  /**
   * Crear nueva pregunta PAES
   */
  static async crearPreguntaPAES(
    data: CreatePreguntaPAESData
  ): Promise<PreguntaPAES> {
    try {
      // Si hay imagen, usar FormData, sino usar JSON
      if (data.imagen && data.imagen instanceof File) {
        const formData = new FormData();

        // Agregar campos al FormData
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === "imagen" && value instanceof File) {
              formData.append(key, value);
            } else if (key === "tags" && Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (key === "alternativas" || key === "datosGrafico") {
              formData.append(
                key,
                typeof value === "string" ? value : JSON.stringify(value)
              );
            } else {
              formData.append(key, String(value));
            }
          }
        });

        const response = await apiClient.post(
          "/simulaciones/preguntas-paes-m1",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // Sin imagen, usar JSON
        const { imagen, ...jsonData } = data;
        const response = await apiClient.post(
          "/simulaciones/preguntas-paes-m1",
          jsonData
        );
        return response.data;
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error al crear pregunta PAES"
      );
    }
  }

  /**
   * Actualizar pregunta PAES
   */
  static async actualizarPreguntaPAES(
    id: number,
    data: UpdatePreguntaPAESData
  ): Promise<PreguntaPAES> {
    try {
      // Si hay imagen, usar FormData, sino usar JSON
      if (data.imagen && data.imagen instanceof File) {
        const formData = new FormData();

        // Agregar campos al FormData
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === "imagen" && value instanceof File) {
              formData.append(key, value);
            } else if (key === "tags" && Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (key === "alternativas" || key === "datosGrafico") {
              formData.append(
                key,
                typeof value === "string" ? value : JSON.stringify(value)
              );
            } else {
              formData.append(key, String(value));
            }
          }
        });

        const response = await apiClient.put(
          `/simulaciones/preguntas-paes-m1/${id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // Sin imagen, usar JSON
        const { imagen, ...jsonData } = data;
        const response = await apiClient.put(
          `/simulaciones/preguntas-paes-m1/${id}`,
          jsonData
        );
        return response.data;
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error al actualizar pregunta PAES"
      );
    }
  }

  /**
   * Eliminar pregunta PAES
   */
  static async eliminarPreguntaPAES(id: number): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(
        `/simulaciones/preguntas-paes-m1/${id}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Error al eliminar pregunta PAES"
      );
    }
  }

  /**
   * Validar formato de pregunta PAES
   */
  static validarPreguntaPAES(data: CreatePreguntaPAESData): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.contenido?.trim()) {
      errors.push("El contenido de la pregunta es requerido");
    }

    if (!data.respuesta?.trim()) {
      errors.push("La respuesta es requerida");
    }

    if (!data.explicacion?.trim()) {
      errors.push("La explicación es requerida");
    }

    if (
      !data.anio ||
      data.anio < 2000 ||
      data.anio > new Date().getFullYear() + 1
    ) {
      errors.push("El año debe ser válido");
    }

    if (!data.periodo?.trim()) {
      errors.push("El período es requerido");
    }

    if (!data.numero || data.numero <= 0) {
      errors.push("El número de pregunta debe ser mayor a 0");
    }

    if (!data.tipo?.trim()) {
      errors.push("El tipo de pregunta es requerido");
    }

    if (!data.asignaturaId || data.asignaturaId <= 0) {
      errors.push("La asignatura es requerida");
    }

    if (data.dificultad && (data.dificultad < 1 || data.dificultad > 5)) {
      errors.push("La dificultad debe estar entre 1 y 5");
    }

    if (data.tiempoEstimado && data.tiempoEstimado <= 0) {
      errors.push("El tiempo estimado debe ser mayor a 0");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Formatear datos de pregunta para mostrar
   */
  static formatearPreguntaPAES(pregunta: PreguntaPAES) {
    return {
      ...pregunta,
      dificultadText: this.getDificultadText(pregunta.dificultad),
      tiempoText: this.formatTiempo(pregunta.tiempoEstimado),
      porcentajeText: `${pregunta.porcentajeCorrectas.toFixed(1)}%`,
    };
  }

  /**
   * Obtener texto de dificultad
   */
  static getDificultadText(dificultad: number): string {
    const dificultades = {
      1: "Muy Fácil",
      2: "Fácil",
      3: "Media",
      4: "Difícil",
      5: "Muy Difícil",
    };
    return dificultades[dificultad as keyof typeof dificultades] || "Media";
  }

  /**
   * Formatear tiempo en segundos a formato legible
   */
  static formatTiempo(segundos: number): string {
    if (segundos < 60) {
      return `${segundos}s`;
    }
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return segs > 0 ? `${minutos}m ${segs}s` : `${minutos}m`;
  }

  /**
   * Obtener color de dificultad
   */
  static getDificultadColor(dificultad: number): string {
    const colores = {
      1: "text-green-600 bg-green-100",
      2: "text-blue-600 bg-blue-100",
      3: "text-yellow-600 bg-yellow-100",
      4: "text-orange-600 bg-orange-100",
      5: "text-red-600 bg-red-100",
    };
    return (
      colores[dificultad as keyof typeof colores] || "text-gray-600 bg-gray-100"
    );
  }
}
